import { useCallback } from 'react';
import { Area, Rack, Shelf, Table, CheckoutZoneData, PolygonEntityType } from '../../app/appContext';
import { CHECKOUT_TYPE_TO_ENTITY, isCheckoutType } from '../../utils/polygonTypes';

interface UseCanvasCallbacksProps {
  areas: Area[];
  standaloneAreas: Area[];
  filteredRacks: Rack[];
  filteredShelves: Shelf[];
  filteredTables: Table[];
  filteredCheckoutZones: CheckoutZoneData[];
  areaMap: { [id: string]: Area };
}

export const useCanvasCallbacks = ({
  areas,
  standaloneAreas,
  filteredRacks,
  filteredShelves,
  filteredTables,
  filteredCheckoutZones,
  areaMap
}: UseCanvasCallbacksProps) => {
  const getArea = useCallback((areaId: string) => {
    return areaMap[String(areaId)];
  }, [areaMap]);

  const getAreaPoints = useCallback((areaId: string): [number, number][] => {
    const area = getArea(areaId);
    return area ? area.points : [];
  }, [getArea]);

  const handlePolygonClick = useCallback((
    polygonId: string,
    type: PolygonEntityType,
    extend: boolean,
    onStartEditing: (polygonId: string, type: PolygonEntityType, points: [number, number][], extend: boolean) => void
  ) => {
    let points: [number, number][] = [];

    if (type === 'standalone') {
      const area = standaloneAreas.find(a => a.id === polygonId);
      if (area) points = area.points;
    } else if (type === 'rack') {
      const rack = filteredRacks.find(r => r.id === polygonId);
      if (rack) points = getAreaPoints(rack.area);
    } else if (type === 'shelf') {
      const shelf = filteredShelves.find(s => s.id === polygonId);
      if (shelf) points = getAreaPoints(shelf.area);
    } else if (type === 'table') {
      const table = filteredTables.find(t => t.id === polygonId);
      if (table) points = getAreaPoints(table.area);
    } else if (isCheckoutType(type)) {
      const cz = filteredCheckoutZones.find(z => z.id === polygonId);
      if (cz && cz.areas.length > 0) {
        points = getAreaPoints(cz.areas[0]);
      }
    }

    onStartEditing(polygonId, type, points, extend);
  }, [standaloneAreas, filteredRacks, filteredShelves, filteredTables, filteredCheckoutZones, getAreaPoints]);

  return {
    getArea,
    getAreaPoints,
    handlePolygonClick,
    CHECKOUT_TYPE_TO_ENTITY
  };
};
